/**
 * Localization: English, Russian and Korean message catalogs plus Intl formatters.
 * Formatters are created once per locale, never inside row renders.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import type { MessageKey } from './i18n-catalog.ts';
export type { MessageKey };

import { createI18n, isLocaleLoaded, loadLocale, type I18n, type Locale } from './i18n-core.ts';
export { createI18n, isLocaleLoaded, loadLocale, type I18n, type Locale };

const I18nContext = createContext<I18n>(createI18n('en'));
/**
 * Shows text in `locale` once its messages are loaded. On first start in Russian or Korean nothing
 * renders until they arrive (no flash of English); when switching, the current language stays
 * until the new one is ready. If loading fails, the chosen language shows with English text.
 */
export function I18nProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const [shown, setShown] = useState<Locale | null>(() => isLocaleLoaded(locale) ? locale : null);
  useEffect(() => {
    if (isLocaleLoaded(locale)) { setShown(locale); return; }
    let live = true;
    void loadLocale(locale).catch(() => undefined).then(() => { if (live) setShown(locale); }); // ignored: falls back to English
    return () => { live = false; };
  }, [locale]);
  const value = useMemo(() => createI18n(shown ?? 'en'), [shown]);
  if (shown === null) return null;
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
export const useI18n = () => useContext(I18nContext);
